import React, { useContext, useEffect } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import Grid from "@mui/material/Grid";

import {
  Authorization,
  ColumnDataType,
  IEntityDetailsProps,
  IEntityTableActionColumn,
  IEntityTableColumn,
  IFilterTemplate,
  PackableAppContext,
  SortOrder,
  authorized,
  formatDate,
} from "packable-components";
import { logDebug } from "packable-components";

import {
  IOrderItem,
  IOrderPageProps,
  IOrderSummary,
  IOrderTableProps,
  OrderItemStatus,
  ShippingStatus,
} from "./Order.types";
import {
  calculateRowsPerPage,
  calculateRowsPerPageSelection,
} from "packable-common";
import OrderDetails from "./OrderDetails";
import OrderTable from "./OrderTable";
import OrderStatus from "./OrderStatus";

export const useStyles = (theme: Theme) => ({
  PackableEntityTableTableHead: {
    "& .MuiTableRow-root": {
      backgroundColor: theme.palette.tableHeader?.primary?.main,
      borderBottom: "solid",
      borderColor: theme.palette.divider,
      "& .MuiTableSortLabel-root": {
        color: theme.palette.tableHeader?.primary?.contrastText,
        fontWeight: theme.typography.body1.fontWeight,
        fontSize: theme.typography.body1.fontSize,
        "&:hover": {
          color: theme.palette.tableHeader?.primary?.contrastText,
        },
        "&:focus": {
          color: theme.palette.tableHeader?.primary?.contrastText,
          fontWeight: "bold",
        },
        "&:active": {
          color: theme.palette.tableHeader?.primary?.contrastText,
        },
        "& .MuiSvgIcon-root": {
          color: theme.palette.tableHeader?.primary?.contrastText,
        },
      },
      "& .MuiTableCell-root": {
        padding: "0.5rem !important",
      },
    },
  },
  PackableEntityTableTableBody: {
    "& .MuiTableRow-root": {
      borderBottom: "solid",
      borderColor: theme.palette.grey[400],
    },
    "& .MuiTableCell-root": {
      padding: "0.5rem !important",
    },
  },
  OrderTableUsedByItemsCountColumn: {
    textAlign: "right",
  },
  OrderTableUsedByItemsCountHeader: {
    textAlign: "right",
  },
  PackableDeleteDialogTitleTypography: {
    muiVariant: "h6",
  },
  PackableDeleteDialogContentTypography: {
    color: theme.palette.text.primary,
  },
  PackableOrderTableItemsStatusCell: {
    display: "flex",
    flexDirection: "column",
  },
  PackableSalectableChip: {
    muiSize: "small" as const,
  },
  PackableOrderTableItemRequestedStatusChip: {
    muiSize: "small" as const,
  },
  PackableOrderTableItemStatusChipIconAttentionCountBadge: {
    muiColor: "error" as const,
  },
  PackableOrderTableItemStatusChipIconInvoCountBadge: {
    muiColor: "info" as const,
  },
  PackableOrderTableItemsStorageEndDateCell: {},
  PackableFilterHeaderTitle: {
    muiVariant: "caption",
  },
});

export const OrderPage: React.FC<IOrderPageProps> = (
  props: IOrderPageProps
): React.JSX.Element => {
  const {
    userAppState,
    clientEntity,
    displayMessage,
    windowHeight = 800,
  } = props;

  logDebug("OrderPage PROPS");
  logDebug(props);

  const theme = useTheme();

  const effectiveStyles = useStyles(theme);
  logDebug("OrderPage Effective Styles:");
  logDebug(effectiveStyles);

  const { httpService, dataFilter, updateDataFilter } =
    useContext(PackableAppContext);

  const updateDataFilterURLs = (inventoryId: string): void => {
    if (isAdminUser()) return;
    let currentDataFilter = dataFilter;
    let templateList: Array<IFilterTemplate> =
      currentDataFilter.filterTemplates;
    let updatedTemplateList = templateList.map((template: IFilterTemplate) => {
      if (
        template.lookupURL !== undefined &&
        template.lookupURL !== null &&
        template.lookupURL.length > 0
      ) {
        const position = template.lookupURL.lastIndexOf("/");
        let newURL: string = template.lookupURL.substring(0, position + 1);
        template.lookupURL = newURL + inventoryId;
      }
      return template;
    });
    if (updatedTemplateList !== undefined) {
      currentDataFilter.filterTemplates =
        updatedTemplateList as Array<IFilterTemplate>;
    }
    /*
    // check if quick filter already active
    const quickFilter = currentDataFilter.selectionFilters.find(
      (filter) => filter.id === "exclude_deleted_and_expired_filter"
    );
    if (quickFilter === undefined) {
      const quickFilterTemplate = currentDataFilter.quickFilters.find(
        (quickFilter) => quickFilter.id === "exclude_deleted_and_expired_filter"
      );
      if (quickFilterTemplate !== undefined) {
        currentDataFilter.selectionFilters.push(
          quickFilterTemplate.filter as ISelectionFilter
        );
        quickFilterTemplate.isCheckedOut = true;
      }
    }
    currentDataFilter.active = true;
    */
    updateDataFilter(currentDataFilter);
  };

  const rowsPerPage = calculateRowsPerPage(windowHeight);
  const rowsPerPageSelection = calculateRowsPerPageSelection(rowsPerPage);

  const orderActionColumns: Array<IEntityTableActionColumn> = [];

  const isAdminUser = (): boolean => {
    return authorized("system", Authorization.MANAGE);
  };

  const getOrderItemStatusList = (
    orderItems: Array<IOrderItem>
  ): Array<OrderItemStatus> => {
    return Object.values(ShippingStatus).map((status) => ({
      status,
      count: orderItems.filter(
        (item) =>
          item.shipmentItem !== null && item.shipmentItem.status === status
      ).length,
    }));
  };

  useEffect(() => {
    updateDataFilterURLs(userAppState.currentInventoryId);
  }, []);

  const renderItemStatuses = (
    row: IOrderSummary,
    className?: string,
    parentStyles?: any
  ): React.JSX.Element => {
    return (
      <Grid
        className="PackableOrderTableItemsStatusCell"
        sx={effectiveStyles.PackableOrderTableItemsStatusCell}
      >
        {row.shippingStatusCounts &&
          row.shippingStatusCounts
            .filter((status) => status.count !== 0)
            .map((status) => {
              return (
                <div style={{ paddingTop: "2px", paddingBottom: "2px" }}>
                  <OrderStatus
                    status={status.status}
                    isAdmin={isAdminUser()}
                    count={status.count}
                  />
                </div>
              );
            })}
      </Grid>
    );
  };

  const renderStorageEndDate = (
    row: IOrderSummary,
    className?: string,
    parentStyles?: any
  ): React.JSX.Element => {
    const minDate = new Date(row.minStorageEndDate);
    const maxDate = new Date(row.maxStorageEndDate);
    const sameDates =
      minDate.getFullYear() === maxDate.getFullYear() &&
      minDate.getMonth() === maxDate.getMonth() &&
      minDate.getDay() === maxDate.getDay();
    return (
      <Grid
        className="PackableOrderTableItemsStorageEndDateCell"
        sx={effectiveStyles.PackableOrderTableItemsStorageEndDateCell}
      >
        {sameDates ? (
          <Grid>
            {formatDate(
              new Date(row.minStorageEndDate).toString(),
              Intl.DateTimeFormat().resolvedOptions().locale,
              { dateStyle: "short" }
            )}
          </Grid>
        ) : (
          <>
            <Grid>
              {formatDate(
                new Date(row.minStorageEndDate).toString(),
                Intl.DateTimeFormat().resolvedOptions().locale,
                { dateStyle: "short" }
              )}
            </Grid>
            <Grid>
              {formatDate(
                new Date(row.maxStorageEndDate).toString(),
                Intl.DateTimeFormat().resolvedOptions().locale,
                { dateStyle: "short" }
              )}
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const orderTableColumns: Array<IEntityTableColumn> = [
    {
      name: "orderNumber",
      title: "Order",
      type: ColumnDataType.string,
    },
    {
      name: "type",
      title: "Type",
      type: ColumnDataType.string,
      cellClassName: "OrderTableTypeColumn",
      headerClassName: "OrderTableTypeHeader",
    },
    {
      name: "status",
      title: "Status",
      type: ColumnDataType.string,
      cellClassName: "OrderTableStatusColumn",
      headerClassName: "OrderTableStatusHeader",
    },
    {
      name: "itemStatuses",
      title: "Items",
      renderFunction: renderItemStatuses,
      type: ColumnDataType.function,
    },
    {
      name: "fromPostalCode",
      title: "Home Location",
      type: ColumnDataType.string,
      cellClassName: "OrderTableFromColumn",
      headerClassName: "OrderTableFromHeader",
    },
    {
      name: "storageEndDateMin",
      title: "Store Until",
      renderFunction: renderStorageEndDate,
      type: ColumnDataType.function,
      cellClassName: "OrderTableStoreUntilColumn",
      headerClassName: "OrderTableStoreUntilHeader",
    },
    {
      name: "userName",
      title: "Customer Name",
      type: ColumnDataType.string,
      cellClassName: "OrderTableUserNameColumn",
      headerClassName: "OrderTableUserNameHeader",
    },
    {
      name: "userEmailAddress",
      title: "Email",
      type: ColumnDataType.string,
      cellClassName: "OrderTableUserEmailAddressColumn",
      headerClassName: "OrderTableUserEmailAddressHeader",
    },
    {
      name: "userPhoneNumber",
      title: "Telephone",
      type: ColumnDataType.string,
      cellClassName: "OrderTableUserPhoneNumberColumn",
      headerClassName: "OrderTableUserPhoneNumberHeader",
    },
    {
      name: "createdByName",
      title: "Created By",
      type: ColumnDataType.string,
      cellClassName: "OrderTableCreatedByNameColumn",
      headerClassName: "OrderTableCreatedByNameHeader",
    },
    {
      name: "createdDate",
      title: "Created",
      type: ColumnDataType.date,
      locale: Intl.DateTimeFormat().resolvedOptions().locale,
      datestyle: { dateStyle: "short", timeStyle: "short" },
      cellClassName: "OrderTableCreatedDateColumn",
      headerClassName: "OrderTableCreatedDateHeader",
    },
    {
      name: "lastModifiedByName",
      title: "Modified By",
      type: ColumnDataType.string,
      cellClassName: "OrderTableLastUpdatedByNameColumn",
      headerClassName: "OrderTableLastUpdatedByNameHeader",
    },
    {
      name: "lastModifiedDate",
      title: "Modified",
      type: ColumnDataType.datetime,
      locale: Intl.DateTimeFormat().resolvedOptions().locale,
      datestyle: { dateStyle: "short", timeStyle: "short" },
      cellClassName: "OrderTableLastUpdatedDateColumn",
      headerClassName: "OrderTableLastUpdatedDateHeader",
    },
  ];

  const orderDisplayDetails = (
    props: IEntityDetailsProps
  ): React.JSX.Element => {
    return <OrderDetails {...props}></OrderDetails>;
  };

  const orderTableProps: IOrderTableProps = {
    httpService: httpService,
    orderBy: "lastModifiedDate",
    order: SortOrder.desc,
    rowsPerPage: rowsPerPage,
    rowsPerPageSelection: rowsPerPageSelection,
    entityName: "order",
    entityTitle: "Orders",
    itemTitle: "Order",
    tableColumns: orderTableColumns,
    actionColumns: orderActionColumns,
    getAllURL: "/order_summary",
    getItemURL: "/order",
    searchURL: "/order",
    updateURL: "/order",
    deleteURL: "/order",
    updateOrderURL: "/order",
    deleteOrderURL: "/order",
    isAdmin: isAdminUser(),
    search: true,
    typeaheadSearch: true,
    userAppState: userAppState,
    clientEntity: clientEntity,
    filter: true,
    dataFilter: dataFilter,
    onFilterUpdate: updateDataFilter,
    onUpdateClick: orderDisplayDetails,
    onRowClick: orderDisplayDetails,
    displayConfirmationMessage: true,
    displayMessage: displayMessage,
    styles: effectiveStyles,
  };

  return (
    <Grid
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        width: "100%",
        pb: 3,
      }}
    >
      <OrderTable {...orderTableProps} />
    </Grid>
  );
};

export default OrderPage;
